import Modal from "react-modal";
import "../style/index.css";
import Loading from "../components/loading";
import Button from "../components/ui/button";
import check from "../assets/images/check.svg";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { DocumentUpload } from "iconsax-reactjs";
import { ChevronDown, CircleCheck } from "lucide-react";
import { toast } from "sonner";
import { handleUploadFileUtils } from "./approveRequest";

const DisburseFundsModal = ({ show, setshow, data }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingAmount, setIsLoadingAmount] = useState(false);
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [formData, setFormData] = useState({
    fileName: "",
    receipt: null,
    sourceOfFunds: "",
  });
  const [amount, setAmount] = useState(0);
  const isPaymentFinance = data?.financeType === "payment_finance";


  const closeModal = () => setshow(false);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      setIsLoadingAmount(true);
      if (!id) return;
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const response = await axios(`/v1/admin/finance/single/${id}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (response.data) {
          const data = await response.data;
          const requestedAmount = data?.data?.loanAmount || 0;
          const offered = data?.data?.loanAmount || requestedAmount;
          setAmount(offered);
        } else {
          console.error("Failed to fetch loan details");
        }
      } catch (error) {
        console.error("Error fetching loan details:", { error });
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Error fetching data",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } finally {
        setIsLoadingAmount(false);
      }
    };

    fetchLoanDetails();
  }, [id]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        offeredAmount: amount,
        percentageToDisburse: 1,
        url: formData?.receipt,
        sourceOfFunds: formData.sourceOfFunds,
      };

      const token = localStorage.getItem("token") || sessionStorage.getItem("token");

      const response = await axios.post(
        `/v1/admin/new-finance/send-fund/${id}`,
        {
          ...payload,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      const result = await response?.data;
      if (response?.data?.error) {
        throw new Error(result.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Funds disbursed successfully!",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      closeModal();
      setTimeout(() => window.location.href = `/finance/${id}`, 300);
    } catch (error) {
      console.error("Error disbursing funds:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to disburse funds",
        text: error?.response?.data?.message || error?.message || "An unexpected error occurred.",
        timer: 4000,
        showConfirmButton: true,
      });
    } finally {
      setIsLoading(false);
    }
  };


  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        fileName: file.name,
      }));
    }
    if (file) {
      const fileTypes = ["JPEG", "JPG", "PNG", "PDF"];
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file?.type)) {
        toast.error("Please upload a JPEG, JPG, PNG, or PDF file");
        return;
      }
    }
    if (file?.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setIsUploading(true);

    try {
      const uploadResponse = await handleUploadFileUtils({
        selectedFile: file,
        name: file.name,
      });
      setFormData((prev) => ({ ...prev, receipt: uploadResponse.link }));
      toast.success("File uploaded successfully");
    } catch (error) {
      console.log({ error })
      toast.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const customStyles = {
    content: {
      margin: "5% auto",
      overflow: "auto",
      maxWidth: "480px",
      maxHeight: "600px",
      height: 'fit-content',
      width: "90%",
      padding: "20px",
      borderRadius: "12px",
      backgroundColor: "#FFFFFF",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
  };

  const isDisabled = isLoadingAmount || isLoading;


  return (
    <Modal
      isOpen={show}
      ariaHideApp={false}
      style={customStyles}
      contentLabel="Accept Payment Modal"
      onRequestClose={closeModal}
    >
      <div className="flex justify-end modal-header">
        <button
          type="button"
          className="material-icons close-icon"
          onClick={closeModal}
          aria-label="Close modal"
        >
          close
        </button>
      </div>
      <div className="flex flex-col ">
        <div className="flex flex-col items-center mb-8">
          <img src={check} alt="check" className="w-10 h-10 mb-4" />

          <h3 className="text-[20px] text-[#101828] font-bold text-center">Disburse funds</h3>
        </div>

        <form onSubmit={handleAccept} className="w-full decline-form">
          <div className="mb-6">
            <div className="text-[#101828] text-[14px] font-semibold mb-2">Amount to disburse</div>
            <div className="bg-[#FBFAFF] p-5 border border-[#E9E7FD] rounded-[13px]">
              <span className="text-[16px] font-bold text-[#101828]">
                {isLoadingAmount ? <Loading /> : `${data?.currency} ${Number(amount).toLocaleString()}`}
              </span>
            </div>
          </div>

          {data?.paymentFinance?.paymentDestination?.address && (
            <div className="mb-6">
              <div className="bg-[#FBFAFF] p-5 border border-[#E9E7FD] rounded-[13px] space-y-4">
                <div>
                  <div className="text-[#6F739B] text-[14px] font-semibold mb-1">Recipient address</div>
                  <div className="text-[16px] font-medium text-[#101828] break-all">
                    {data?.paymentFinance?.paymentDestination?.address}
                  </div>
                </div>
                <div>
                  <div className="text-[#6F739B] text-[14px] font-semibold mb-1">Network</div>
                  <div className="text-[16px] font-medium text-[#101828]">
                    {data?.paymentFinance?.paymentDestination?.network}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <div className="text-[#101828] text-[14px] font-semibold mb-2">Source of funds</div>
            <div className="relative">
              <select
              id="sourceOfFunds"
              name="sourceOfFunds"
              value={formData.sourceOfFunds}
              onChange={(e) => setFormData((prev) => ({ ...prev, sourceOfFunds: e.target.value }))}
              required
              disabled={isDisabled}
              className="w-full appearance-none bg-[#FBFAFF] py-4 pl-4 pr-12 border border-[#E9E7FD] rounded-[13px] text-[14px] text-[#101828]"
            >
              <option value="" disabled>Select source of funds</option>
              <option value="Credit Coop">Credit Coop</option>
              <option value="Cicada Partners">Cicada Partners</option>
              <option value="Access x Oxygen">Access x Oxygen</option>
              <option value="Tradevu">Tradevu</option>
              </select>
              <ChevronDown
                aria-hidden="true"
                className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#101828]"
              />
            </div>
          </div>

          {/* <div className="mb-6">
            <div className="text-[#101828] text-[14px] font-semibold mb-2">Payment receipt</div>
            <div className="space-y-2">
              <div className="flex items-center relative cursor-pointer justify-between gap-4 !bg-[#ECEFF3] w-full !rounded-[8px] h-[56px] shadow-none !border-0 px-3 py-2 text-base">
                <input
                  type="file"
                  onChange={handleFileChange}
                  format="image/*,.pdf"
                  className="absolute w-full h-full opacity-0 cursor-pointer"
                />
                <span className="flex items-center">
                  <DocumentUpload className="mr-2 size-6" />
                  {isUploading
                    ? "Uploading..."
                    : formData?.fileName
                      ? formData?.fileName
                      : "Upload payment receipt"}
                </span>
              </div>

              {!isUploading && formData.receipt && (
                <p className="flex items-center gap-1 text-sm text-success-10">
                  {" "}
                  <CircleCheck /> File uploaded successfully
                </p>
              )}
            </div>
          </div> */}

          {/* <div className="flex items-start gap-4 mb-8">
            <div
              className="relative inline-flex items-center cursor-pointer select-none"
              onClick={() => setApprovalRequired(!approvalRequired)}
            >
              <input type="checkbox" className="sr-only" checked={approvalRequired} readOnly />
              <div
                className={`w-14 h-7 rounded-full transition-colors duration-200 ${
                  approvalRequired ? "bg-[#6F00FF]" : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute top-[2px] left-[2px] h-[24px] w-[24px] bg-white rounded-full transition-transform duration-200 ${
                    approvalRequired ? "translate-x-7" : "translate-x-0"
                  }`}
                ></div>
              </div>
            </div>

            <div>
              <h4 className="text-[#383B56] text-[14px] font-semibold mb-1">
                Approval to drawdown
              </h4>
              <p className="text-[#6F739B] text-[14px]">
                Payment from their credit wallet will require admin approval
              </p>
            </div>
          </div> */}

          <Button
            type="submit"
            className="w-full p-4 rounded-[10px] bg-[#EBE4FF] font-bold text-[18px] text-[#6F00FF] hover:bg-[#d9d0ff] hover:text-[#6F00FF]"
            disabled={isDisabled}
          >
            {isLoading ? <Loading /> : "Disburse funds"}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default DisburseFundsModal;
